import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, TennisBall } from "@phosphor-icons/react";
import confetti from "canvas-confetti";
import CameraFeed from "./CameraFeed";
import WinnerCelebration from "./WinnerCelebration";

const BG = "https://images.pexels.com/photos/35525977/pexels-photo-35525977.jpeg?auto=compress&cs=tinysrgb&w=1600";

function teamName(t) { return t.p2 ? `${t.p1} / ${t.p2}` : t.p1; }

function pickNameClass(maxLen, hasCamera) {
  const tiers = hasCamera
    ? ["text-3xl sm:text-5xl lg:text-6xl", "text-3xl sm:text-4xl lg:text-5xl", "text-2xl sm:text-3xl lg:text-4xl"]
    : ["text-5xl sm:text-7xl lg:text-8xl", "text-4xl sm:text-6xl lg:text-7xl", "text-3xl sm:text-5xl lg:text-6xl"];
  if (maxLen > 26) return tiers[2];
  if (maxLen > 18) return tiers[1];
  return tiers[0];
}

function Row({ team, sets, games, points, accent, winner, nameCls, ptsCls, ptsW, serving, servingPlayer }) {
  const color = accent === "green" ? "#CCFF00" : "#00D1FF";
  const effPlayer = !winner && serving ? (servingPlayer === 2 && !team.p2 ? 1 : servingPlayer) : null;
  return (
    <div className="flex items-center justify-between gap-4 sm:gap-6 w-full">
      <div className="flex items-center gap-3 min-w-0 flex-1" style={{ color: winner ? color : "#fff" }}>
        {winner && <Trophy size={40} weight="fill" style={{ color }} className="shrink-0" />}
        <div className={`flex flex-col min-w-0 font-heading font-extrabold uppercase ${nameCls} leading-[0.95]`}>
          <span className="truncate flex items-center gap-3">
            {effPlayer === 1 && <TennisBall size={34} weight="fill" className="text-padel-green shrink-0 animate-pulse-glow" />}
            {team.p1}
          </span>
          {team.p2 && (
            <span className="truncate flex items-center gap-3">
              {effPlayer === 2 && <TennisBall size={34} weight="fill" className="text-padel-green shrink-0 animate-pulse-glow" />}
              {team.p2}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 sm:gap-10 shrink-0">
        <span className="font-heading text-4xl sm:text-6xl tabular-nums text-muted-foreground w-12 sm:w-16 text-center leading-none">{sets}</span>
        <span className="font-heading text-4xl sm:text-6xl tabular-nums text-white w-12 sm:w-16 text-center leading-none">{games}</span>
        <AnimatePresence mode="popLayout">
          <motion.span
            key={points}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: [1.4, 1], opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className={`font-heading ${ptsCls} font-extrabold tabular-nums ${ptsW} text-center leading-none`}
            style={{ color, textShadow: `0 0 40px ${color}cc` }}
          >
            {points}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}

// Full-size live scoreboard for a court. Used by the per-match fullscreen overlay
// and by the per-court public screen page.
export default function CourtScoreboard({ match, court, headerRight }) {
  const prevWinner = useRef(null);
  useEffect(() => {
    const w = match?.score?.winner;
    if (w && prevWinner.current !== w) {
      prevWinner.current = w;
      confetti({ particleCount: 220, spread: 120, origin: { y: 0.4 }, colors: ["#CCFF00", "#00D1FF", "#fff"] });
    }
  }, [match]);

  const live = match && match.status === "live";
  const hasCamera = court && court.camera_type && court.camera_type !== "none" && court.camera_url && court.camera_enabled !== false;
  const courtName = court?.name || match?.court_name || "";

  let body;
  if (match) {
    const s = match.score;
    const longest = Math.max(
      (match.team1.p1 || "").length, (match.team1.p2 || "").length,
      (match.team2.p1 || "").length, (match.team2.p2 || "").length
    );
    const nameCls = pickNameClass(longest, hasCamera);
    const ptsCls = hasCamera ? "text-[14vw] sm:text-[9vw] lg:text-[8vw]" : "text-[16vw] sm:text-[12vw] lg:text-[10vw]";
    const ptsW = hasCamera ? "w-[22vw] sm:w-[14vw] lg:w-[12vw]" : "w-[26vw] sm:w-[18vw] lg:w-[15vw]";
    body = (
      <div className={`relative z-10 flex-1 min-h-0 flex flex-col ${hasCamera ? "lg:flex-row" : ""} items-stretch gap-6 px-6 sm:px-12 pb-8`}>
        {hasCamera && (
          <div data-testid="court-camera" className="lg:w-[46%] shrink-0 bg-black border border-white/10 overflow-hidden h-[38vh] lg:h-auto">
            <CameraFeed type={court.camera_type} url={court.camera_url} />
          </div>
        )}
        <div className="flex-1 min-w-0 flex flex-col justify-center gap-8">
          <div className="flex justify-end gap-4 sm:gap-10 text-sm tracking-[0.25em] uppercase text-muted-foreground font-bold pr-1">
            <span className="w-12 sm:w-16 text-center">Set</span>
            <span className="w-12 sm:w-16 text-center">Gm</span>
            <span className={`${ptsW} text-center`}>{s.in_tiebreak ? "TB" : "Pts"}</span>
          </div>
          <Row team={match.team1} sets={s.sets1} games={s.games1} points={s.points1} accent="blue" winner={s.winner === 1} nameCls={nameCls} ptsCls={ptsCls} ptsW={ptsW} serving={live && s.serving === 1} servingPlayer={s.serving_player} />
          <div className="h-px bg-white/10" />
          <Row team={match.team2} sets={s.sets2} games={s.games2} points={s.points2} accent="green" winner={s.winner === 2} nameCls={nameCls} ptsCls={ptsCls} ptsW={ptsW} serving={live && s.serving === 2} servingPlayer={s.serving_player} />
        </div>
      </div>
    );
  } else {
    body = (
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center gap-6 px-8">
        {hasCamera && (
          <div className="w-full max-w-3xl bg-black border border-white/10 overflow-hidden aspect-video">
            <CameraFeed type={court.camera_type} url={court.camera_url} />
          </div>
        )}
        <p data-testid="court-no-match" className="font-heading text-3xl sm:text-5xl uppercase tracking-widest text-muted-foreground text-center">Sin partido en esta cancha</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full flex flex-col overflow-hidden bg-[#0A0A0C] grain">
      <div className="absolute inset-0 z-0">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-[#0A0A0C]/85" />
      </div>

      <div className="relative z-40 flex items-center justify-between px-8 py-5 shrink-0">
        <div className="flex items-center gap-3">
          <TennisBall size={36} weight="fill" className="text-padel-green" />
          <span className="font-heading text-2xl sm:text-4xl font-extrabold uppercase tracking-wide">{courtName}</span>
          {live ? (
            <span className="ml-3 flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-padel-green">
              <span className="w-2.5 h-2.5 rounded-full bg-padel-green animate-pulse-glow" /> En vivo
            </span>
          ) : (
            <span className="ml-3 text-xs tracking-[0.2em] uppercase text-muted-foreground">{match ? (match.status === "finished" ? "Finalizado" : "Programado") : "Libre"}</span>
          )}
        </div>
        {headerRight}
      </div>

      {body}

      {match && match.score.winner && (
        <WinnerCelebration
          team={match.score.winner === 1 ? match.team1 : match.team2}
          accent={match.score.winner === 1 ? "blue" : "green"}
          size="lg"
        />
      )}
    </div>
  );
}
